import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, breadcrumb } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { FAQ } from "@/components/FAQ";
import DevisForm from "@/components/DevisForm";
import { SectionTitle, DataTable, LabelledList, EventPageHero } from "@/components/PageBlocks";
import { ArrowRight, Check } from "lucide-react";
import { ANNIVERSAIRE_CONTENT } from "@/content/anniversaire";
import { usePageContent } from "@/lib/pageContent";
import { pageImageUrl } from "@/content/pageImages";
import { Rich } from "@/components/Rich";

const SITE = "https://lentracterestaurant.fr";

const ANNIV_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE}/anniversaire#service`,
  "name": "Anniversaire et fête privée à L'Entracte",
  "serviceType": "Privatisation de restaurant pour anniversaire et fête de famille",
  "description":
    "Privatisation d'un hôtel particulier à Ville-d'Avray pour anniversaires, fiançailles, noces et départs en retraite : dîner assis, cocktail dînatoire ou déjeuner en terrasse.",
  "provider": { "@id": `${SITE}/#lieu` },
  "areaServed": [
    { "@type": "City", "name": "Ville-d'Avray" },
    { "@type": "City", "name": "Versailles" },
    { "@type": "City", "name": "Sèvres" },
    { "@type": "City", "name": "Saint-Cloud" },
    { "@type": "City", "name": "Boulogne-Billancourt" },
    { "@type": "City", "name": "Paris" },
  ],
};

export default function Anniversaire() {
  const { lang, localePath, alternateUrls } = useI18n();
  const c = usePageContent("anniversaire", lang === "en" ? "en" : "fr", ANNIVERSAIRE_CONTENT[lang === "en" ? "en" : "fr"]);
  const [imgs, setImgs] = useState({});

  useSEO({
    title: c.meta.title,
    description: c.meta.description,
    keywords: c.meta.keywords,
    lang,
    alternates: alternateUrls(),
    jsonLd: [
      breadcrumb([
        { name: lang === "en" ? "Home" : "Accueil", url: `${SITE}/` },
        { name: lang === "en" ? "Events" : "Événements", url: `${SITE}/evenements` },
        { name: lang === "en" ? "Birthday" : "Anniversaire", url: `${SITE}/anniversaire` },
      ]),
      ANNIV_SERVICE_LD,
    ],
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => setImgs(r.data || {})).catch(() => {});
  }, []);

  const HERO = pageImageUrl(imgs, "anniversaire", "hero");
  const FORMAT_IMGS = [
    pageImageUrl(imgs, "anniversaire", "format_1"),
    pageImageUrl(imgs, "anniversaire", "format_2"),
    pageImageUrl(imgs, "anniversaire", "format_3"),
  ];

  const crumbs = c.breadcrumb.map((b) => ({ label: b.label, to: b.to ? localePath(b.to) : undefined }));

  return (
    <div data-testid="anniversaire-page">
      <EventPageHero
        overline={c.overline}
        h1={c.h1}
        intro={c.intro}
        ctaDevis={c.ctaDevis}
        ctaCall={c.ctaCall}
        image={HERO}
        alt={c.heroAlt}
        crumbs={crumbs}
        phoneSource="anniversaire_hero"
      />

      {/* EN BREF */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1100px] mx-auto">
          <SectionTitle>{c.brief.title}</SectionTitle>
          <div className="mt-10">
            <DataTable caption={c.brief.caption} rows={c.brief.rows} testid="anniversaire-brief-table" />
          </div>
        </div>
      </section>

      {/* OCCASIONS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.occasions.title}</SectionTitle>
          <div className="mt-10">
            <LabelledList items={c.occasions.items} testid="anniversaire-occasions" />
          </div>
          <Link
            to={localePath("/mariage")}
            data-testid="anniversaire-mariage-link"
            className="btn-ghost mt-10 inline-flex"
          >
            {c.occasions.weddingLinkLabel} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FORMATS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1500px] mx-auto">
          <SectionTitle>{c.formats.title}</SectionTitle>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            {c.formats.items.map((f, i) => (
              <article key={f.name} className="border border-[#D9D3C5] bg-[#FFFFFF]">
                <div className="aspect-[4/3] overflow-hidden">
                  {FORMAT_IMGS[i] && (
                    <img src={FORMAT_IMGS[i]} alt={f.alt} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl text-[#203240] font-light">{f.name}</h3>
                  <Rich as="p" value={f.body} className="mt-4 text-[#3D4A5A] leading-relaxed" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CE QUI EST COMPRIS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.included.title}</SectionTitle>
          <ul className="mt-10 space-y-5">
            {c.included.items.map((it) => (
              <li key={it} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
                <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
                <Rich value={it} />
              </li>
            ))}
          </ul>
          <div className="mt-12">
            <DataTable
              caption={c.included.caption}
              rows={c.included.rows}
              testid="anniversaire-extras-table"
            />
          </div>
        </div>
      </section>

      {/* MOINS DE 12 */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#203240]">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-[#F6F0E7] font-light">{c.small.title}</h2>
          <p className="mt-8 text-[#F6F0E7]/80 leading-relaxed text-base md:text-lg">
            {c.small.body1}{" "}
            <Link
              to={localePath(c.small.link1)}
              data-testid="anniversaire-reservation-link"
              className="text-[#E6CFA1] underline underline-offset-4 hover:text-white transition-colors"
            >
              {c.small.link1Label}
            </Link>{" "}
            {c.small.body2}{" "}
            <Link
              to={localePath(c.small.link2)}
              data-testid="anniversaire-giftcard-link"
              className="text-[#E6CFA1] underline underline-offset-4 hover:text-white transition-colors"
            >
              {c.small.link2Label}
            </Link>
            .
          </p>
        </div>
      </section>

      {/* ACCÈS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.access.title}</SectionTitle>
          <Rich as="p" value={c.access.intro} className="mt-8 text-[#3D4A5A] leading-relaxed" />
          <p className="mt-6 text-[#697585] leading-relaxed">{c.access.note}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#FFFFFF]">
        <div className="max-w-[1000px] mx-auto">
          <SectionTitle>{c.faqTitle}</SectionTitle>
          <div className="mt-10">
            <FAQ items={c.faq} testid="anniversaire-faq" schemaId="ld-faq-anniversaire" />
          </div>
        </div>
      </section>

      {/* DEVIS */}
      <section id="devis" className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#F6F0E7] scroll-mt-32">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline">{c.overline}</p>
            <SectionTitle>{c.devis.title}</SectionTitle>
            <Rich as="p" value={c.devis.body} className="mt-8 text-[#3D4A5A] leading-relaxed" />
            <p className="mt-6 text-[#697585]">{c.devis.contact}</p>
          </div>
          <div className="md:col-span-7">
            <div className="border border-[#D9D3C5] bg-[#FFFFFF] p-7 md:p-10">
              <DevisForm defaultEventType={c.eventType} pageSource="/anniversaire" />
            </div>
          </div>
        </div>
      </section>

      {/* LIENS INTERNES */}
      <section className="py-16 px-6 md:px-12 lg:px-16 bg-[#FFFFFF] border-t border-[#D9D3C5]">
        <div className="max-w-[1500px] mx-auto">
          <p className="overline">{c.related.title}</p>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            {c.related.links.map((l) => (
              <Link
                key={l.to}
                to={localePath(l.to)}
                className="inline-flex items-center gap-2 text-[#203240] hover:text-[#B8956A] transition-colors"
              >
                {l.label} <ArrowRight size={14} className="text-[#B8956A]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
